import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export const ChangePasswordModal = () => {
  const { isChangingPassword, setIsChangingPassword, mustChangePassword, changePassword } = useAuth();
  const { darkMode } = useTheme();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  if (!isChangingPassword && !mustChangePassword) return null;

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      setError('Пароль должен быть не менее 6 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    if (!mustChangePassword && !currentPassword.trim()) {
      setError('Введите текущий пароль');
      return;
    }

    const result = await changePassword(currentPassword, newPassword);
    if (result.success) {
      alert('✅ Пароль успешно изменен!');
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    } else {
      setError(result.error || 'Ошибка при смене пароля');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={() => !mustChangePassword && setIsChangingPassword(false)}
    >
      <div
        className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-100'} p-2 rounded-lg`}>
              <Lock className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {mustChangePassword ? 'Требуется смена пароля' : 'Сменить пароль'}
            </h2>
          </div>
          {!mustChangePassword && (
            <button
              onClick={() => setIsChangingPassword(false)}
              className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        <div className="space-y-4">
          {error && (
            <div className={`p-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-600'}`}>
              {error}
            </div>
          )}

          {!mustChangePassword && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Текущий пароль
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                  }`}
                  placeholder="Введите текущий пароль"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Новый пароль
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
                placeholder="Введите новый пароль (минимум 6 символов)"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Подтвердите новый пароль
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                }`}
                placeholder="Повторите новый пароль"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            {!mustChangePassword && (
              <button
                onClick={() => setIsChangingPassword(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Отмена
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={newPassword.length < 6 || newPassword !== confirmPassword || (!mustChangePassword && !currentPassword.trim())}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
              } ${
                (newPassword.length < 6 || newPassword !== confirmPassword || (!mustChangePassword && !currentPassword.trim()))
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              <Lock className="w-4 h-4 mr-2" />
              {mustChangePassword ? 'Установить пароль' : 'Сохранить'}
            </button>
          </div>

          {mustChangePassword && (
            <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-yellow-900/30 border border-yellow-800' : 'bg-yellow-50'}`}>
              <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                ⚠️ <strong>Важно:</strong> Это ваш первый вход в систему. Пожалуйста, установите новый пароль для защиты вашей учетной записи.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};